import React from 'react'
import Flex from '@jarvis.oddle/core/components/Flex'
import styled, { css } from 'styled-components'
import {
  margin,
  marginSizes,
  footer,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import Divider from '@jarvis.oddle/core/components/Divider'
import { LinkText, P } from '@jarvis.oddle/core/components/Typo'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import modalController from 'src/decorators/ModalController'
import { ODDLE_BACK_LINK } from 'config/client'
import { languages } from '@jarvis.oddle/core/tools/localisation'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import IconGlobe from '@jarvis.oddle/core/components/Icons/IconGlobe'
import { GhostButton } from 'src/components/molecules/Modals/MobileLanguageDrawer/MobileLanguageDrawer'
import InlineTextLink from 'src/components/core/InlineTextLink'
import ShopLink from 'src/components/molecules/ShopLink'

const MAX_LOCALE_OPTIONS = 2

const linkCss = css<{ normalised?: boolean }>`
  text-decoration: none;
  margin: ${(props) =>
    props.normalised ? 0 : `0 ${margin[marginSizes.small]}`};
  &:hover {
    opacity: ${footer.linkOpacityOnHover};
  }
`

const StyledInlineTextLink = styled(InlineTextLink)`
  ${linkCss}
`

const StyledLinkText = styled(LinkText)`
  ${linkCss}
`

// TODO: fix LocaleLink with new router
const StyledLocaleLink = styled(ShopLink)`
  color: ${(props) => props.theme.colorHeadingsFont};
  ${linkCss}
`

const VerticalDivider = styled(Divider)`
  height: 12px;
  width: 1px;
  margin: 0 ${margin[marginSizes.large]};
  border-top: 0;
  border-left: 1px solid ${(props) => props.theme.colorBodyFont};
  ${breakpoint('desktop')`
    margin: 0 ${margin[marginSizes.small]};
  `}
`

const BasicFooterContainer = styled(Flex).attrs(() => ({
  alignItems: 'center',
  paddingTop: paddingSizes.large
}))`
  flex-direction: column;
  justify-content: center;
  padding-bottom: 36px;
  ${breakpoint('desktop')`
    flex-direction: row;
    justify-content: start;
    padding-bottom: ${padding[paddingSizes.large]}
  `}
`

const FooterLinkContainer = styled(Flex)`
  align-items: baseline;
  margin-top: ${margin[marginSizes.medium]};
  margin-bottom: ${margin[marginSizes.small]};
  ${breakpoint('desktop')`
    margin: 0 0 0 auto;
  `}
`

const FooterLocaleContainer = styled(Flex)`
  align-items: center;
  justify-content: center;
  margin-top: ${margin[marginSizes.small]};
  ${breakpoint('desktop')`
    margin-top: 0;
  `}
`

const FooterBrandTag = styled(Flex)`
  align-items: baseline;
  margin-left: 0;
  margin-bottom: 6px;
  ${breakpoint('desktop')`
    margin-left: ${margin[marginSizes.small]};
    margin-bottom: 0;
  `}
`

const generateLocalesOptions = (locales, buttonText, onClick) => {
  const section = []

  for (let i = 0; i < locales.length; i++) {
    if (i > 0) {
      section.push(<VerticalDivider key={`d-${i}`} />)
    }

    if (i === MAX_LOCALE_OPTIONS && locales.length > MAX_LOCALE_OPTIONS + 1) {
      section.push(
        <GhostButton key={`b-${i}`} onClick={onClick}>
          {buttonText}
        </GhostButton>
      )
      break
    }

    section.push(
      <StyledLocaleLink key={`a-${i}`} locale={locales[i]} normalised="true">
        {languages[locales[i]]}
      </StyledLocaleLink>
    )
  }

  return section
}

interface BasicContentProps {
  previewMode: boolean
  locales: string[]
  locale: string
}

const BasicContent = ({
  previewMode,
  locales,
  locale
}: BasicContentProps): JSX.Element => {
  const { t } = React.useContext(TranslationContext)

  const handleOnClick = (e) => {
    if (!previewMode) return
    e.preventDefault()
    window.confirm(
      t(
        'This link cannot be opened in customisation preview. It will be opened in a new window (_target_).\nClick OK to continue',
        { target: ODDLE_BACK_LINK }
      )
    ) && window.open(ODDLE_BACK_LINK, '_blank')
  }

  const handleLocaleOnClick = () => {
    modalController.showModal('LanguageSelectionModal', {
      slideInAnimation: 'bottom',
      fullWidth: true
    })
  }

  return (
    <BasicFooterContainer>
      <Flex alignItems='center'>
        <IconGlobe iconSize='medium' />
        <P marginLeft={marginSizes.small} marginRight={marginSizes.small}>
          {t('Page offered in:')}
        </P>
      </Flex>
      <FooterLocaleContainer>
        {generateLocalesOptions(locales, t('Show more'), handleLocaleOnClick)}
      </FooterLocaleContainer>
      <FooterLinkContainer>
        <StyledInlineTextLink
          path='/[locale]/terms'
          pathAs={`/${locale}/terms`}
          text={t('Terms & Conditions')}
        />
        <StyledInlineTextLink
          path='/[locale]/privacy'
          pathAs={`/${locale}/privacy`}
          text={t('Privacy Policy')}
        />
      </FooterLinkContainer>
      <FooterBrandTag>
        <P marginRight={marginSizes.extraSmall} normalised>
          {t('Powered by')}
        </P>
        <a href={ODDLE_BACK_LINK || '#'} onClick={handleOnClick}>
          <StyledLinkText bold normalised heading>
            {t('Oddle.me')}
          </StyledLinkText>
        </a>
      </FooterBrandTag>
    </BasicFooterContainer>
  )
}

export default BasicContent
